"use client";

import * as React from "react";
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CORE_COLORS, CORE_FONT } from "@/lib/theme/core";

const WEEKDAYS = ["日", "一", "二", "三", "四", "五", "六"];
const MONTHS = [
  "1月", "2月", "3月", "4月", "5月", "6月",
  "7月", "8月", "9月", "10月", "11月", "12月",
];

function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
export function sameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
export function formatDateZh(d: Date) {
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
}

/** Today at 00:00 — the default upper bound for a publication date. */
export function today() {
  const n = new Date();
  return new Date(n.getFullYear(), n.getMonth(), n.getDate());
}

/** Five years back from today — the default lower bound. */
export function fiveYearsAgo() {
  const t = today();
  return new Date(t.getFullYear() - 5, t.getMonth(), t.getDate());
}

function monthStartsBefore(a: Date, b: Date) {
  return a.getFullYear() < b.getFullYear()
    || (a.getFullYear() === b.getFullYear() && a.getMonth() < b.getMonth());
}

/**
 * Month-grid date picker.
 *
 * Several controls looked like date pickers but had no handler and no calendar:
 * the 出版日 button in `components/shared/main/category-header.tsx`, the
 * live-news date button, and `DateField` in the property chart panel — which
 * rendered a plain `<span>`. QA: "1. 懸停時，將游標圖示改為手指圖示。
 * 2. 應該顯示一個日期選擇器元件來進行篩選" and "沒有反應 2. 點擊時沒有出現日期選擇器".
 *
 * Controlled and presentation-only: the parent owns `value` and decides what a
 * date *does*. Built on the same `components/ui/popover.tsx` shell as
 * `FilterSelect` so the two controls behave identically.
 */
export function DatePicker({
  value,
  onChange,
  label,
  min,
  max,
  className = "",
  children,
}: {
  value: Date | null;
  onChange: (next: Date) => void;
  /** Accessible name, and the placeholder when nothing is chosen. */
  label: string;
  min?: Date;
  max?: Date;
  className?: string;
  /** Custom trigger. Omit for the default bordered button. */
  children?: React.ReactNode;
}) {
  const [open, setOpen] = React.useState(false);
  const [month, setMonth] = React.useState(() => startOfMonth(value ?? new Date()));
  // Month/year panel. QA on the first attempt: "the year selection is bugged …
  // missing next or prev page of years selection, missing month selection".
  // The panel now pages the YEAR with its own chevrons and offers a 12-month
  // grid, so any month in range is two clicks away.
  const [panel, setPanel] = React.useState(false);
  const [panelYear, setPanelYear] = React.useState(() => (value ?? new Date()).getFullYear());

  React.useEffect(() => {
    if (open) {
      setMonth(startOfMonth(value ?? new Date()));
      setPanel(false);
      setPanelYear((value ?? new Date()).getFullYear());
    }
  }, [open, value]);

  const first = startOfMonth(month);
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const leading = first.getDay();
  const cells: (Date | null)[] = [
    ...Array.from({ length: leading }, () => null),
    ...Array.from(
      { length: daysInMonth },
      (_, i) => new Date(month.getFullYear(), month.getMonth(), i + 1)
    ),
  ];

  const outOfRange = (d: Date) =>
    (min !== undefined && d < min) || (max !== undefined && d > max);

  // Month stepping stops at the range edges instead of walking into months
  // where every day is disabled.
  const prevDisabled =
    min !== undefined && monthStartsBefore(new Date(month.getFullYear(), month.getMonth() - 1, 1), startOfMonth(min));
  const nextDisabled =
    max !== undefined && monthStartsBefore(startOfMonth(max), new Date(month.getFullYear(), month.getMonth() + 1, 1));

  const firstYear = min ? min.getFullYear() : month.getFullYear() - 5;
  const lastYear = max ? max.getFullYear() : month.getFullYear();
  const monthOutOfRange = (y: number, m: number) => {
    if (min && (y < min.getFullYear() || (y === min.getFullYear() && m < min.getMonth()))) return true;
    if (max && (y > max.getFullYear() || (y === max.getFullYear() && m > max.getMonth()))) return true;
    return false;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {children ?? (
          <button
            type="button"
            aria-label={label}
            aria-haspopup="dialog"
            aria-expanded={open}
            className={`inline-flex items-center gap-2 bg-white transition-colors ${className}`}
            style={{
              padding: "10px 12px",
              border: `1px solid ${CORE_COLORS.dividerSoft}`,
              height: 48,
              fontFamily: CORE_FONT.sans,
              fontSize: 14,
              color: value ? CORE_COLORS.textPrimary : CORE_COLORS.textSecondary,
            }}
          >
            <CalendarDays className="size-4 shrink-0" color={CORE_COLORS.textSecondary} />
            <span className="truncate">{value ? formatDateZh(value) : label}</span>
          </button>
        )}
      </PopoverTrigger>

      {/* `components/ui/popover.tsx` sets no background, so the calendar used to
          render straight onto whatever was behind it — QA: the panel "merged"
          with the page. Surface, border, radius and shadow are set here. */}
      <PopoverContent
        align="start"
        className="w-auto rounded-[6px] border p-[12px] shadow-lg"
        style={{
          backgroundColor: "#ffffff",
          borderColor: CORE_COLORS.dividerSoft,
          boxShadow: "0 8px 24px rgba(0,0,0,0.14)",
        }}
      >
        <div className="flex items-center justify-between pb-[8px]">
          <button
            type="button"
            aria-label={panel ? "上一年" : "上個月"}
            disabled={panel ? panelYear - 1 < firstYear : prevDisabled}
            onClick={() =>
              panel
                ? setPanelYear((y) => y - 1)
                : setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))
            }
            className="inline-flex size-7 items-center justify-center rounded-sm transition-colors disabled:opacity-30"
          >
            <ChevronLeft className="size-4" strokeWidth={2} />
          </button>
          <button
            type="button"
            aria-haspopup="dialog"
            aria-expanded={panel}
            aria-label={
              panel ? `選擇月份，目前 ${panelYear}年` : `選擇年月，目前 ${month.getFullYear()}年${month.getMonth() + 1}月`
            }
            onClick={() => {
              setPanelYear(month.getFullYear());
              setPanel((v) => !v);
            }}
            className="inline-flex items-center gap-[4px] rounded-sm px-[6px] py-[2px] transition-colors"
            style={{
              fontFamily: CORE_FONT.sans,
              fontSize: 14,
              fontWeight: 700,
              color: CORE_COLORS.textPrimary,
            }}
          >
            {panel ? `${panelYear}年` : `${month.getFullYear()}年${month.getMonth() + 1}月`}
            <ChevronDown
              className="size-3.5 transition-transform"
              strokeWidth={2}
              style={{ transform: panel ? "rotate(180deg)" : undefined }}
            />
          </button>
          <button
            type="button"
            aria-label={panel ? "下一年" : "下個月"}
            disabled={panel ? panelYear + 1 > lastYear : nextDisabled}
            onClick={() =>
              panel
                ? setPanelYear((y) => y + 1)
                : setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))
            }
            className="inline-flex size-7 items-center justify-center rounded-sm transition-colors disabled:opacity-30"
          >
            <ChevronRight className="size-4" strokeWidth={2} />
          </button>
        </div>

        {panel && (
          <ul
            role="listbox"
            aria-label={`${panelYear}年的月份`}
            className="grid grid-cols-3 gap-[4px]"
            style={{ width: 7 * 32 + 6 * 2 }}
          >
            {MONTHS.map((label, m) => {
              const active = panelYear === month.getFullYear() && m === month.getMonth();
              const disabled = monthOutOfRange(panelYear, m);
              return (
                <li key={label}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    disabled={disabled}
                    onClick={() => {
                      setMonth(new Date(panelYear, m, 1));
                      setPanel(false);
                    }}
                    className="flex h-9 w-full items-center justify-center rounded-sm transition-colors disabled:opacity-30"
                    style={{
                      fontFamily: CORE_FONT.sans,
                      fontSize: 13,
                      fontWeight: active ? 700 : 500,
                      color: active ? "#ffffff" : CORE_COLORS.textPrimary,
                      backgroundColor: active ? CORE_COLORS.brandGreen : undefined,
                    }}
                  >
                    {label}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <div className="grid grid-cols-7 gap-[2px]" hidden={panel}>
          {WEEKDAYS.map((w) => (
            <span
              key={w}
              className="flex size-8 items-center justify-center"
              style={{
                fontFamily: CORE_FONT.sans,
                fontSize: 11,
                color: CORE_COLORS.textTertiary,
              }}
            >
              {w}
            </span>
          ))}
          {cells.map((d, i) =>
            d === null ? (
              <span key={`pad-${i}`} className="size-8" />
            ) : (
              <button
                key={d.toISOString()}
                type="button"
                disabled={outOfRange(d)}
                aria-pressed={!!value && sameDay(d, value)}
                onClick={() => {
                  onChange(d);
                  setOpen(false);
                }}
                className="flex size-8 items-center justify-center rounded-sm transition-colors hover:text-[#56917d] disabled:cursor-not-allowed disabled:opacity-30"
                style={{
                  fontFamily: CORE_FONT.sans,
                  fontSize: 13,
                  color:
                    value && sameDay(d, value)
                      ? "#ffffff"
                      : CORE_COLORS.textPrimary,
                  backgroundColor:
                    value && sameDay(d, value) ? CORE_COLORS.brandGreen : undefined,
                }}
              >
                {d.getDate()}
              </button>
            )
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
